import { Stack, Typography } from '@mui/material';
import { useForm } from 'react-hook-form';

import { useGetUpcomingEvents, UpcomingEvent } from '@/api/endpoints/calendar/useGetUpcomingEvents';
import { usePropertyManager } from '@/context/PropertyManagerProvider';

import FormSelect from '../form/FormSelect';

import DashboardEventsList, { EventItem } from './DashboardEventsList';

const DAYS_OPTIONS = [7, 14, 30, 60, 90].map(days => ({
  label: `${days} days`,
  value: days,
}));

interface FormData {
  days: number;
}

const getEventTitle = (event: UpcomingEvent): string => {
  if (event.title) {
    return event.title;
  }

  if (event.description) {
    return event.description;
  }

  if (event.event_type === 'lease') {
    return 'Lease Event';
  }

  if (event.event_type === 'bill') {
    return 'Bill Due';
  }

  return 'Upcoming Event';
};

const getEventDescription = (event: UpcomingEvent): string => {
  const parts: string[] = [];

  // Add building/unit info if available
  if (event.building?.name) {
    parts.push(`Building: ${event.building.name}`);
  }

  if (event.unit?.name) {
    parts.push(`Unit: ${event.unit.name}`);
  }

  // Add tenant info if available
  if (event.tenant?.full_name) {
    parts.push(`Tenant: ${event.tenant.full_name}`);
  }

  // Add event type for AI events
  if (event.source === 'ai' && event.event_type) {
    parts.push(`Type: ${event.event_type}`);
  }

  return parts.length > 0 ? parts.join(' • ') : event.description || 'No additional details';
};

const DashboardUpcomingEvents: React.FC = () => {
  const { selectedPropertyLocation } = usePropertyManager();

  const { watch, control, register } = useForm<FormData>({
    defaultValues: {
      days: 30,
    },
  });

  const daysAhead = watch('days');

  const { data: upcomingEvents, isLoading } = useGetUpcomingEvents(
    selectedPropertyLocation?.id,
    daysAhead,
    !!selectedPropertyLocation?.id
  );

  // Transform events to EventItem format
  const eventItems: EventItem[] = (upcomingEvents || []).map(event => ({
    id: typeof event.id === 'string' ? parseInt(event.id, 10) || 0 : event.id,
    title: getEventTitle(event),
    description: getEventDescription(event),
    eventDate: event.start || event.event_date || new Date().toISOString(),
    eventType: event.event_type,
    source: event.source,
    documentId: event.external_document_id,
    building: event.building,
    unit: event.unit,
    tenant: event.tenant,
    allDay: event.all_day,
  }));

  // if (!eventItems.length && !isLoading) return null;

  return (
    <DashboardEventsList title="Upcoming Events" items={eventItems} isLoading={isLoading}>
      <Stack direction="row" alignItems="center" spacing={3}>
        <Typography
          variant="subtitle3"
          sx={{
            whiteSpace: 'nowrap',
          }}
        >
          Next
        </Typography>

        <FormSelect
          placeholder="Select Days"
          options={DAYS_OPTIONS}
          control={control}
          {...register('days')}
        />
      </Stack>
    </DashboardEventsList>
  );
};

export default DashboardUpcomingEvents;
